/**
 * Sistema de toasts nativo (heredado de la antigua Daysi Vista).
 *
 * La vista clásica monta BulkUploader sin <ToastProvider>, así que ahí
 * useToast() devuelve no-ops; el uploader reporta en su propio log.
 *
 * Uso:
 *   const toast = useToast();
 *   toast.success('Guardado');
 *   toast.error('Error al guardar', 'Detalle opcional');
 *
 * El Provider debe envolver la sección de la app que vaya a consumir el hook.
 */

import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';

const ToastContext = createContext(null);

let _seq = 0;
const nextId = () => `t-${++_seq}-${Date.now()}`;

const DEFAULT_DURATIONS = {
  success: 3500,
  info: 3500,
  warning: 5000,
  error: 6000,
};

// ─── Iconos (SVG, mismo lenguaje visual que el resto del módulo) ────
const Icons = {
  success: () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="8" cy="8" r="6.5" />
      <path d="M5 8.3 L7.2 10.5 L11 6" />
    </svg>
  ),
  error: () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
      <circle cx="8" cy="8" r="6.5" />
      <path d="M5.5 5.5 L10.5 10.5 M10.5 5.5 L5.5 10.5" />
    </svg>
  ),
  warning: () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M8 1.5 L14.5 13.5 L1.5 13.5 Z" />
      <path d="M8 6 L8 9.5 M8 11.5 L8 12" />
    </svg>
  ),
  info: () => (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
      <circle cx="8" cy="8" r="6.5" />
      <path d="M8 7 L8 11.5 M8 4.5 L8 5" />
    </svg>
  ),
  close: () => (
    <svg width="11" height="11" viewBox="0 0 11 11" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
      <path d="M2 2 L9 9 M9 2 L2 9" />
    </svg>
  ),
};

function ToastItem({ toast, onDismiss }) {
  const Icon = Icons[toast.type] || Icons.info;
  return (
    <div className={`toast toast-${toast.type}`} role="status" aria-live="polite">
      <span className="toast-icon">{Icon()}</span>
      <div className="toast-body">
        <div className="toast-message">{toast.message}</div>
        {toast.description && <div className="toast-description">{toast.description}</div>}
      </div>
      <button className="toast-close" onClick={onDismiss} title="Cerrar">
        {Icons.close()}
      </button>
    </div>
  );
}

function ToastContainer({ toasts, dismiss }) {
  if (toasts.length === 0) return null;
  return (
    <div className="toast-container">
      {toasts.map(t => (
        <ToastItem key={t.id} toast={t} onDismiss={() => dismiss(t.id)} />
      ))}
    </div>
  );
}

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  // Mapa id → timeoutId para poder cancelar al desmontar o al dismiss manual.
  const timersRef = useRef(new Map());

  const dismiss = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
    const timer = timersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }
  }, []);

  const show = useCallback((type, message, description, opts = {}) => {
    const id = nextId();
    const duration = opts.duration ?? DEFAULT_DURATIONS[type] ?? 4000;
    setToasts(prev => [...prev, { id, type, message, description }]);
    if (duration > 0) {
      const timer = setTimeout(() => dismiss(id), duration);
      timersRef.current.set(id, timer);
    }
    return id;
  }, [dismiss]);

  // Limpieza al desmontar el Provider
  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      timers.forEach(t => clearTimeout(t));
      timers.clear();
    };
  }, []);

  const api = {
    success: (msg, desc, opts) => show('success', msg, desc, opts),
    error:   (msg, desc, opts) => show('error', msg, desc, opts),
    info:    (msg, desc, opts) => show('info', msg, desc, opts),
    warning: (msg, desc, opts) => show('warning', msg, desc, opts),
    dismiss,
  };

  // Puente global para que efectos fuera del árbol del Provider (p. ej.
  // notificaciones al cargar la app) puedan disparar toasts sin tener que
  // mover el hook a un componente padre. Se limpia al desmontar el provider.
  useEffect(() => {
    window.__daysiToast = api;
    return () => {
      if (window.__daysiToast === api) delete window.__daysiToast;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <ToastContainer toasts={toasts} dismiss={dismiss} />
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    // Fallback silencioso para evitar romper la app si alguien usa el hook
    // fuera del Provider. En desarrollo es útil ver el warning.
    if (process.env.NODE_ENV !== 'production') {
      console.warn('useToast() usado fuera de <ToastProvider>; los toasts no se mostrarán.');
    }
    const noop = () => null;
    return { success: noop, error: noop, info: noop, warning: noop, dismiss: noop };
  }
  return ctx;
}
